// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { BillingPlanListItem } from '@/billing/api/types';
import {
  EventAvailableOutlined,
  ProjectIcon,
  ScheduleOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';

interface Props {
  menuAnchor: HTMLElement | null;
  menuPlan: BillingPlanListItem | null;
  onClose: () => void;
  onConfirmPlan: () => void;
  onViewBillingPlan: (plan: BillingPlanListItem) => void;
}

// Triple-dot row menu on the billing plans listing.
//
// "Activate billing plan" is hidden once a plan is active because active
// plan dates can no longer be edited.
//
// "View billing plan" pops the same drawer the project detail page
// uses, in place — no navigation. The parent owns the open state so
// the drawer stays mounted at the route root (render-at-root rule).
export function BillingPlanActionMenu(props: Props) {
  const navigate = useNavigate();
  const plan = props.menuPlan;
  const open = Boolean(props.menuAnchor) && plan !== null;

  const handleConfirmPlan = () => {
    props.onConfirmPlan();
    props.onClose();
  };

  const handleViewBillingPlan = () => {
    if (plan) {
      props.onViewBillingPlan(plan);
    }
    props.onClose();
  };

  const handleViewProject = () => {
    if (plan) {
      void navigate({
        to: '/projects/$projectKey',
        params: { projectKey: plan.projectKey },
      });
    }
    props.onClose();
  };

  const isPending = plan?.status === 'PENDING';

  return (
    <Menu
      anchorEl={props.menuAnchor}
      open={open}
      onClose={props.onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      {isPending && (
        <Tooltip
          arrow
          title={
            'Plan is already activated. Edit the activation date from the line item drawer.'
          }
          placement="left"
          disableHoverListener={isPending}
        >
          <span>
            <MenuItem onClick={handleConfirmPlan} disabled={!isPending}>
              <ListItemIcon>
                <EventAvailableOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText>Activate billing plan</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
      )}
      <MenuItem onClick={handleViewBillingPlan}>
        <ListItemIcon>
          <ScheduleOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>View billing plan</ListItemText>
      </MenuItem>
      <MenuItem onClick={handleViewProject}>
        <ListItemIcon>
          <ProjectIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>View project</ListItemText>
      </MenuItem>
    </Menu>
  );
}
